/**
 * Fields of a type data sheet (shared vehicle knowledge, CLAUDE.md). Grouped for display,
 * important ones first and confirmed more strictly (keep IMPORTANT equal to
 * public.vehicle_fact_important). "many" fields hold a list (faults, tips), the others
 * one value each, though competing values may stand side by side until mechanics decide.
 * Texts: `fact.<key>` in every locale.
 */
export type FactGroup = "oilChange" | "fluids" | "service" | "asked" | "media" | "torques" | "electrics" | "wheels" | "faults";
export const FACT_GROUPS: readonly FactGroup[] = ["oilChange", "fluids", "service", "asked", "media", "faults", "torques", "electrics", "wheels"];

export interface FactField {
  key: string;
  group: FactGroup;
  important?: boolean;
  many?: boolean;
  /** In the basic AI lookup of a type (owner's decision, 2026-10-31: fluids, specs, maintenance plan). */
  base?: boolean;
}

export const FACT_FIELDS: readonly FactField[] = [
  // the oil change (owner's mockup, 2026-11-01): everything needed for it in one place
  { key: "oilCapacity", group: "oilChange", important: true, base: true },
  { key: "oilSpec", group: "oilChange", important: true, base: true },
  { key: "oilFilter", group: "oilChange", important: true, base: true },
  { key: "oilDrainPlug", group: "oilChange", important: true, base: true },
  { key: "serviceInterval", group: "oilChange", important: true, base: true },
  // every other fluid of the type: quantity, specification and change interval in one cell
  { key: "gearboxManual", group: "fluids", important: true, base: true },
  { key: "gearboxAuto", group: "fluids", important: true, base: true },
  // the earlier single gearbox row, kept readable for existing data
  { key: "gearboxOil", group: "fluids", important: true },
  { key: "transferDiff", group: "fluids", base: true },
  { key: "brakeFluid", group: "fluids", important: true, base: true },
  { key: "coolant", group: "fluids", important: true, base: true },
  { key: "powerSteering", group: "fluids", base: true },
  { key: "adblue", group: "fluids", base: true },
  // the maintenance plan: what to replace when
  { key: "maintenance", group: "service", many: true, base: true },
  // found for a mechanic's own question ("label: value")
  { key: "custom", group: "asked", many: true },
  // quick lookups ("rowKey: value", rows of AI_PACKS)
  { key: "pTiming", group: "asked", many: true },
  { key: "pTorques", group: "asked", many: true },
  { key: "pFuses", group: "asked", many: true },
  { key: "pTyres", group: "asked", many: true },
  { key: "pAircon", group: "asked", many: true },
  // pictures and documents: only the title and the link to the source (we never keep the file)
  { key: "diagram", group: "media", many: true },
  // asked for only when needed (later optional lookups)
  { key: "timing", group: "service" },
  { key: "torqueWheel", group: "torques" },
  { key: "torqueDrain", group: "torques" },
  { key: "torqueSpark", group: "torques" },
  { key: "torqueOther", group: "torques", many: true },
  { key: "commonFault", group: "faults", many: true },
  { key: "tip", group: "faults", many: true },
  { key: "fuseBox", group: "electrics" },
  // the own fuse sheet (owner, 2026-11-01): the whole fuse and relay table as one shared claim,
  // compact JSON (FuseTable below), drawn by Garagevo; only the AI lookup writes it
  { key: "fuseTable", group: "electrics", important: true },
  // the own timing sheet (owner, 2026-11-01): a short structured summary of the timing replacement
  // in the workshop's language (TimingSheet below), drawn by Garagevo; only the AI lookup writes it
  { key: "timingSheet", group: "service", important: true },
  { key: "tyres", group: "wheels" },
];

export const IMPORTANT_FACTS: readonly string[] = FACT_FIELDS.filter((f) => f.important).map((f) => f.key);

/** Fields of the basic AI lookup, in display order. */
export const BASE_FACTS: readonly string[] = FACT_FIELDS.filter((f) => f.base).map((f) => f.key);

/** Display order of a field (FACT_FIELDS order; unknown ones last). */
export function factOrder(key: string): number {
  const i = FACT_FIELDS.findIndex((f) => f.key === key);
  return i < 0 ? FACT_FIELDS.length : i;
}

export function factField(key: string): FactField | undefined {
  return FACT_FIELDS.find((f) => f.key === key);
}

/** Vehicle data needed to put a vehicle into the type tree by its own data. */
export const VEHICLE_TYPE_FIELDS = ["make", "model", "engineCode"] as const;

export function missingTypeFields(v: { make?: string | null; model?: string | null; engineCode?: string | null }): string[] {
  return VEHICLE_TYPE_FIELDS.filter((k) => !(v[k] ?? "").trim());
}

// ---- type tree names (keep equal to the SQL functions in 20261114000000_vehicle_type_names.sql) ----

/** "SWIFT III (MZ, EZ) 1.3 (RS 413)" → "SWIFT III (MZ, EZ)" */
export function vehicleModelName(model: string): string {
  return model.replace(/\s+\d[.,]\d.*$/, "").trim() || model.trim();
}

/** "HFX (TU1JP), hfz (TU1JP)" → ["HFX", "HFZ"] */
export function vehicleEngineCodes(engine: string | null | undefined): string[] {
  const codes = (engine ?? "")
    .split(/[,;/]/)
    .map((c) => c.replace(/\([^)]*\)/g, "").replace(/\s+/g, "").toUpperCase())
    .filter(Boolean);
  return [...new Set(codes)].sort();
}

/** "1.3 · M13A · 68 kW" */
export function vehicleEngineLabel(model: string, engine: string | null | undefined, kw: number | null | undefined): string {
  const displacement = model.match(/\s(\d[.,]\d)/)?.[1];
  const codes = vehicleEngineCodes(engine).join(", ");
  return [displacement, codes || undefined, kw ? `${kw} kW` : undefined].filter(Boolean).join(" · ");
}

/**
 * Quick lookups in the "Ask the AI" box (owner's decision, 2026-11-01): fixed data packs, no
 * text typed in for the mechanic; the program sends a fixed query and the rows come back as
 * facts of `field` ("rowKey: value"). Only what can really be found publicly (no wiring
 * diagrams). Texts: `pack.<key>` and `packRow.<row>` in every locale. Keep equal to
 * AI_PACKS in supabase/functions/_shared/aiVehicle.ts (a test checks it).
 */
export interface AiPack {
  key: string;
  field: string;
  rows: readonly string[];
  /** Must find a diagram (more searching, so it costs more); equal to mediaRequired on the server. */
  diagram?: boolean;
  /**
   * The answer is one structured table kept as a single fact of `field` (the fuse sheet), not
   * rows; known sources are read first, which makes it cheaper. Equal to `sheet` on the server.
   */
  sheet?: boolean;
  /**
   * A second step writes a sheet of its own from the best sources (the timing sheet): one fact of
   * this field, shown instead of the rows when there is one. Equal to `sheetField` on the server.
   */
  sheetField?: string;
}
export const AI_PACKS: readonly AiPack[] = [
  { key: "timing", field: "pTiming", diagram: true, sheetField: "timingSheet", rows: ["timingKind", "timingInterval", "timingReplaceWith", "timingTool"] },
  { key: "torques", field: "pTorques", rows: ["torqueWheelBolts", "torqueDrainPlug", "torqueSparkPlugs", "torqueCylinderHead", "torqueCrankPulley", "torqueCaliperCarrier", "torqueOilFilterHousing", "torqueFlywheel"] },
  { key: "fuses", field: "fuseTable", sheet: true, rows: [] },
  { key: "tyres", field: "pTyres", rows: ["tyreSizes", "tyrePressureFront", "tyrePressureRear"] },
  { key: "aircon", field: "pAircon", rows: ["airconRefrigerant", "airconOil"] },
];

/** Rows of earlier packs, still on some sheets (the fuse pack had three text rows before the fuse sheet). */
const LEGACY_PACK_ROWS: readonly string[] = ["fuseBoxes", "fuseMain", "fuseRelays"];

/** "torqueWheelBolts: 120 Nm" → ["torqueWheelBolts", "120 Nm"] for a pack row, else null. */
export function packRow(value: string): [string, string] | null {
  const m = value.match(/^([a-zA-Z]{3,40}):\s*(.+)$/s);
  return m && (AI_PACKS.some((p) => p.rows.includes(m[1]!)) || LEGACY_PACK_ROWS.includes(m[1]!)) ? [m[1]!, m[2]!] : null;
}

// ---- the own fuse sheet (owner, 2026-11-01) ----

/** One fuse: its number as printed ("12", "F12", "SB1"), amperage ("10", "7,5", "25/30", "" = reserve) and what it protects. */
export interface FuseEntry {
  no: string;
  amp: string;
  what: string;
}
export interface FuseBox {
  name: string;
  location: string;
  fuses: FuseEntry[];
}
export interface FuseRelay {
  position: string;
  what: string;
}
/** A source of the table: the main one (its values were taken) and the ones compared with it. */
export interface FuseSource {
  url: string;
  role: "main" | "compared";
}
/** The value of a "fuseTable" fact (compact JSON; written by cleanFuseTable on the server). */
export interface FuseTable {
  /** Years and variants it is valid for, e.g. "2004–2013, hatchback and estate". */
  years: string;
  boxes: FuseBox[];
  relays: FuseRelay[];
  sources: FuseSource[];
}

const text = (v: unknown, max: number) => (typeof v === "string" ? v.trim().slice(0, max) : "");

/** The fuse sheet of a "fuseTable" fact, or null when the value is not one (never throws). */
export function parseFuseTable(value: string): FuseTable | null {
  let raw: unknown;
  try {
    raw = JSON.parse(value);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const list = (v: unknown) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") as Record<string, unknown>[] : []);
  const boxes = list(r.boxes).map((b) => ({
    name: text(b.name, 120),
    location: text(b.location, 300),
    fuses: list(b.fuses).map((f) => ({ no: text(f.no, 8), amp: text(f.amp, 16), what: text(f.what, 200) })).filter((f) => f.no),
  })).filter((b) => b.fuses.length > 0);
  if (boxes.length === 0) return null;
  const sources = list(r.sources)
    .map((s) => ({ url: text(s.url, 500), role: s.role === "main" ? "main" as const : "compared" as const }))
    .filter((s) => /^https?:\/\//.test(s.url));
  return {
    years: text(r.years, 200),
    boxes,
    relays: list(r.relays).map((x) => ({ position: text(x.position, 80), what: text(x.what, 200) })).filter((x) => x.position || x.what),
    sources,
  };
}

// ---- the own timing sheet (owner, 2026-11-01) ----

/** The value of a "timingSheet" fact (compact JSON; written by cleanTimingSheet on the server). */
export interface TimingSheet {
  /** "belt", "chain", "gear" or "" when the source does not say. */
  drive: "belt" | "chain" | "gear" | "";
  interval: string;
  /** Parts replaced with it (tensioner, idlers, water pump ...). */
  parts: string[];
  /** Locking and setting tools, with their numbers when given. */
  tools: string[];
  /** The setting procedure, at most 8 short steps. */
  steps: string[];
  /** At most 6 torques: what, value (with the angle steps). */
  torques: { what: string; value: string }[];
  warnings: string[];
  /** Years and engines it is valid for. */
  years: string;
  sources: FuseSource[];
}

/** The timing sheet of a "timingSheet" fact, or null when the value is not one (never throws). */
export function parseTimingSheet(value: string): TimingSheet | null {
  let raw: unknown;
  try {
    raw = JSON.parse(value);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const texts = (v: unknown, n: number, max: number) => (Array.isArray(v) ? v.map((x) => text(x, max)).filter(Boolean).slice(0, n) : []);
  const torques = (Array.isArray(r.torques) ? r.torques : [])
    .map((x) => (Array.isArray(x) ? { what: text(x[0], 100), value: text(x[1], 80) } : x && typeof x === "object" ? { what: text((x as Record<string, unknown>).what, 100), value: text((x as Record<string, unknown>).value, 80) } : null))
    .filter((x): x is { what: string; value: string } => !!x && !!x.what && !!x.value)
    .slice(0, 6);
  const steps = texts(r.steps, 8, 240);
  if (steps.length === 0 && torques.length === 0) return null;
  const drive = r.drive === "belt" || r.drive === "chain" || r.drive === "gear" ? r.drive : "";
  const sources = (Array.isArray(r.sources) ? r.sources : [])
    .filter((x) => x && typeof x === "object")
    .map((s) => ({ url: text((s as Record<string, unknown>).url, 500), role: (s as Record<string, unknown>).role === "main" ? "main" as const : "compared" as const }))
    .filter((s) => /^https?:\/\//.test(s.url));
  return {
    drive,
    interval: text(r.interval, 160),
    parts: texts(r.parts, 6, 100),
    tools: texts(r.tools, 4, 140),
    steps,
    torques,
    warnings: texts(r.warnings, 3, 240),
    years: text(r.years, 200),
    sources,
  };
}

/**
 * The standard colours of blade fuses by amperage (mini, ATO and maxi blade fuses use the same
 * code up to 40 A; 50 A and 60 A are maxi). Keys use a decimal comma.
 */
export const FUSE_COLOURS: Readonly<Record<string, string>> = {
  "1": "#1F1F1F", "2": "#9AA4AC", "3": "#7B4FB5", "4": "#F2A7C3", "5": "#C9A97A", "7,5": "#8B5A2B",
  "10": "#D7263D", "15": "#2F6FD6", "20": "#F2C200", "25": "#E8E8E8", "30": "#2EA043", "40": "#FF8C1A",
  "50": "#D7263D", "60": "#2F6FD6",
};

/** The colour key of an amperage: its first value ("25/30" → "25", "7.5" → "7,5"); "" for a reserve or an unknown one. */
export function fuseColourKey(amp: string): string {
  const first = amp.split("/")[0]!.trim().replace(/\s*A$/i, "").replace(".", ",");
  return FUSE_COLOURS[first] ? first : "";
}

/** Whether text on the fuse colour must be dark (light fuse colours). */
export function fuseColourLight(key: string): boolean {
  return ["", "2", "4", "5", "20", "25"].includes(key);
}

// ---- pictures and documents (owner's request, 2026-11-01) ----

/**
 * A diagram, picture or PDF on the source's own page: we keep only its title, the link and a
 * preview link loaded from the source (copyright: no file is ever copied or stored). The value
 * of a "diagram" fact is its title, optionally after the pack it belongs to ("fuses: Fuse box
 * layout"); without a pack it belongs to the basic sheet.
 */
export type MediaKind = "image" | "pdf" | "page";

/** The kind by the URL's extension (keep equal to mediaKind in supabase/functions/_shared/aiVehicle.ts). */
export function mediaKind(url: string): MediaKind {
  let path = "";
  try {
    path = new URL(url).pathname.toLowerCase();
  } catch {
    return "page";
  }
  if (/\.pdf$/.test(path)) return "pdf";
  if (/\.(jpe?g|png|webp|gif|avif|svg)$/.test(path)) return "image";
  return "page";
}

/** Packs that are no longer offered (the "faults" tile, removed 2026-11-01): their pictures stay general ones. */
const LEGACY_MEDIA_PACKS: readonly string[] = ["faults"];

/** "fuses: Fuse box layout" → { pack: "fuses", title: "Fuse box layout" }; no pack prefix → pack null. */
export function mediaRow(value: string): { pack: string | null; title: string } {
  const m = value.match(/^([a-zA-Z]{3,20}):\s*(.+)$/s);
  if (m && LEGACY_MEDIA_PACKS.includes(m[1]!)) return { pack: null, title: m[2]! };
  return m && AI_PACKS.some((p) => p.key === m[1]) ? { pack: m[1]!, title: m[2]! } : { pack: null, title: value };
}

// ---- since the last oil change of THIS vehicle (its own work orders, never shared) ----

const OIL_WORDS = new Set(["olaj", "olajcsere", "motorolaj", "motorolajcsere", "oil", "motoröl", "ölwechsel", "motorolej", "motorový", "ulje"]);
// other oils are not an engine oil change
const NOT_ENGINE_OIL = new Set(["váltó", "váltóolaj", "gear", "gearbox", "transmission", "atf", "getriebe", "getriebeöl", "differenciál", "differential", "szervo", "servo", "hydraulic", "hidraulika", "fék", "brake"]);

/** Whether a work order line is an engine oil change (a part or labour line naming engine oil). */
export function isOilChangeLine(line: { kind: string; description: string }): boolean {
  if (line.kind !== "part" && line.kind !== "labour") return false;
  const words = line.description.toLowerCase().split(/[^\p{L}]+/u).filter(Boolean);
  return words.some((w) => OIL_WORDS.has(w)) && !words.some((w) => NOT_ENGINE_OIL.has(w));
}

export interface SinceOilChange {
  /** Kilometres since then (null when a km reading is missing). */
  km: number | null;
  days: number;
  closedAt: string;
  orderNumber: string;
}

/**
 * Km and time since the last closed work order of the vehicle with an oil change line.
 * orders: the vehicle's work orders with their lines; null when none had an oil change.
 */
export function sinceOilChange(
  orders: readonly { status: string; closedAt: string | null; odometerKm: number | null; number: string; items: readonly { kind: string; description: string }[] }[],
  currentKm: number | null | undefined,
  today: Date = new Date(),
): SinceOilChange | null {
  const last = orders
    .filter((o) => o.status === "closed" && o.closedAt && o.items.some(isOilChangeLine))
    .sort((a, b) => b.closedAt!.localeCompare(a.closedAt!))[0];
  if (!last) return null;
  const days = Math.max(0, Math.floor((today.getTime() - new Date(last.closedAt!).getTime()) / 86_400_000));
  const km = currentKm != null && last.odometerKm != null && currentKm >= last.odometerKm ? currentKm - last.odometerKm : null;
  return { km, days, closedAt: last.closedAt!, orderNumber: last.number };
}
